import type { SerializedError } from '@reduxjs/toolkit';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { skipToken } from '@reduxjs/toolkit/query';
import type { JSX } from 'react';
import { useContext } from 'react';
import { Navigate, useLocation, useSearchParams } from 'react-router';

import {
    useLoadCentralInstancesQuery,
    useLoadLicenseOrgsQuery,
} from '../../apiClients/entitlementsClient/requests/EntitlementsAPI';
import { useLazyRefreshAccessTokenQuery } from '../../apiClients/identityClient/requests/AccessTokenApi';
import { LOGIN_ERROR_ROUTE, LOGIN_ROUTE, LOGOUT_SUCCESS_ROUTE } from '../../constants';
import {
    clearCombinedToken as clearAccessToken,
    getCombinedToken as getAccessToken,
} from '../../store/sessionStorageHelpers/accessTokenHelper/accessTokenHelper';
import { clearCSRFState } from '../../store/sessionStorageHelpers/authStateHelpers';
import { setDeeplink } from '../../store/sessionStorageHelpers/deeplinkHelper/deeplinkHelper';
import { clearEntitlements } from '../../store/sessionStorageHelpers/entitlementsHelper/entitlementsHelper';
import { clearPkceVerifier } from '../../store/sessionStorageHelpers/pkceFlowHelpers';
import { LoginContext } from '../loginHandler/LoginContext';

function accessTokenInvalid(error: FetchBaseQueryError | SerializedError | undefined) {
    if (error) {
        // It is possible that if the user refreshes the page fast enough, that the original
        // request will be cancelled and RTK Query will return the
        // vague error: { status: 'FETCH_ERROR', error: 'TypeError: Failed to fetch' }
        // In this case, the access token is not invalid, and we do not want to redirect the user
        // back to the login page.
        const { status } = error as any;
        if (status && typeof status === 'number' && status >= 400) {
            return true;
        }
    }
    return false;
}

function accessTokenUnauthorized(error: FetchBaseQueryError | SerializedError | undefined) {
    if (error) {
        const { status } = error as any;
        if (status && typeof status === 'number' && status === 401) {
            return true;
        }
    }
    return false;
}

function clearSession() {
    clearAccessToken();
    clearCSRFState();
    clearEntitlements();
    clearPkceVerifier();
}

export function AccessTokenGuard({ children }: { children: JSX.Element }) {
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const { forceOauthPrefix, includeClientSecret, service } = useContext(LoginContext);
    const [refreshToken, refreshTokenResult] = useLazyRefreshAccessTokenQuery();

    const authCode = searchParams.get('code');
    const errorCode = searchParams.get('error');
    const accessToken = getAccessToken();

    // Check that our access token still works. We may have been logged out of another window, so
    // our auth will no longer apply.
    if (accessToken) {
        const { error } =
            service === 'central'
                ? useLoadCentralInstancesQuery()
                : useLoadLicenseOrgsQuery(service !== 'none' ? { service } : skipToken);

        // Attempt to refresh the accessToken since the LicenseToken refresh failed
        if (accessTokenUnauthorized(error) && accessToken.refresh_token) {
            refreshToken({
                forceOauthPrefix,
                includeClientSecret,
                refreshToken: accessToken.refresh_token,
            });
        } else if (accessTokenInvalid(error)) {
            clearSession();
            return <Navigate to={LOGIN_ROUTE} />;
        }
    }

    const expired = accessToken && accessToken.expires_at <= Date.now();
    const noAccessToken = !accessToken?.access_token && !authCode;
    const notLoginRoute =
        location.pathname !== LOGIN_ROUTE &&
        location.pathname !== LOGIN_ERROR_ROUTE &&
        location.pathname !== LOGOUT_SUCCESS_ROUTE;

    if ((expired || noAccessToken || refreshTokenResult?.isError) && notLoginRoute) {
        // Redirect them to the /login page, but save the current location they were
        // trying to go to when they were redirected.
        clearSession();

        const regex = 'login';

        if (errorCode) {
            searchParams.delete('error');
            searchParams.delete('error_description');
            const searchString = searchParams.toString();
            const deepLink = `${location.pathname}${
                searchString.length > 0 ? `?${searchString}` : ''
            }`;
            if (!regex.match(location.pathname)) {
                setDeeplink(deepLink);
            }
        } else if (!regex.match(location.pathname)) {
            setDeeplink(`${location.pathname}${location.search}`);
        }

        return <Navigate to={LOGIN_ROUTE} state={{ from: location }} replace />;
    }

    return children;
}
